// テキストレイヤーのラスタライズ (OffscreenCanvas 2D)。
// 戻り値は文書座標での矩形と RGBA。縦書きは 1 文字ずつ描き、ラテン文字は 90° 回転する。

import type { TextLayer } from '@/types/document'
import type { Rect } from '@/types/geometry'
import { layoutText, type LayoutLine } from './layout'
import { cssFont } from './fonts'
import type { RgbaImage } from '@/pixels/transform'
import { warpRaster } from './warp'

export interface TextRaster {
  rect: Rect
  image: RgbaImage
  lines: LayoutLine[]
}

let measureCanvas: OffscreenCanvas | null = null
function ctx2d(): OffscreenCanvasRenderingContext2D {
  if (!measureCanvas) measureCanvas = new OffscreenCanvas(1, 1)
  return measureCanvas.getContext('2d')!
}

const isUpright = (ch: string) => /[　-ヿ㐀-鿿豈-﫿＀-￯]/.test(ch)
const VERT_PUNCT: Record<string, string> = { '、': '︑', '。': '︒', '「': '﹁', '」': '﹂', '『': '﹃', '』': '﹄', '（': '︵', '）': '︶', '［': '﹇', '］': '﹈', '｛': '︷', '｝': '︸', '〈': '︿', '〉': '﹀', '《': '︽', '》': '︾', '【': '︻', '】': '︼', '…': '︙', '—': '︱', 'ー': '丨', '－': '︲', '：': '︓', '；': '︔' }

export function rasterizeText(layer: TextLayer): TextRaster | null {
  const r = rasterizeTextPlain(layer)
  if (!r || !layer.warp) return r
  const w = warpRaster(r.image, r.rect, layer.warp)
  return { rect: w.rect, image: w.image, lines: r.lines }
}

function rasterizeTextPlain(layer: TextLayer): TextRaster | null {
  const st = layer.style
  const ctx = ctx2d()
  ctx.font = cssFont(st)
  const measure = (s: string) => ctx.measureText(s).width
  const lineH = st.fontSize * st.lineHeight
  if (st.orientation === 'horizontal') {
    const lines = layoutText(layer.text, measure, st.letterSpacing, layer.boxWidth)
    const maxW = Math.max(1, ...lines.map(l => l.width), layer.boxWidth ?? 0)
    const ascent = st.fontSize * 1.0, descent = st.fontSize * 0.3
    const h = Math.ceil(lineH * (lines.length - 1) + ascent + descent)
    const w = Math.ceil(maxW + st.fontSize) // 斜体などのはみ出し余白
    const alignX = st.align === 'left' ? 0 : st.align === 'center' ? (layer.boxWidth ?? maxW) / 2 : (layer.boxWidth ?? maxW)
    const originX = Math.floor(layer.position.x - alignX - st.fontSize / 2)
    const originY = Math.floor(layer.position.y - ascent)
    const canvas = new OffscreenCanvas(w, h)
    const c = canvas.getContext('2d')!
    c.font = cssFont(st)
    c.fillStyle = st.hex
    c.textBaseline = 'alphabetic'
    c.letterSpacing = `${st.letterSpacing}px`
    lines.forEach((ln, i) => {
      const y = layer.position.y - originY + i * lineH
      let x = layer.position.x - originX
      if (st.align === 'center') x -= ln.width / 2
      else if (st.align === 'right') x -= ln.width
      c.textAlign = 'left'
      c.fillText(ln.text, x, y)
    })
    const img = c.getImageData(0, 0, w, h)
    return { rect: { x: originX, y: originY, w, h }, image: { width: w, height: h, data: img.data }, lines }
  }
  // 縦書き: 各文字を縦に並べ、列を右から左へ
  const cols = layoutText(layer.text, s => s.length * st.fontSize, st.letterSpacing, layer.boxWidth)
  const colW = lineH
  const advance = st.fontSize + st.letterSpacing
  const maxChars = Math.max(1, ...cols.map(c => c.text.length))
  const w = Math.ceil(colW * cols.length + st.fontSize)
  const h = Math.ceil(advance * maxChars + st.fontSize * 0.5)
  const originX = Math.floor(layer.position.x - w + st.fontSize)
  const originY = Math.floor(layer.position.y - st.fontSize * 0.2)
  const canvas = new OffscreenCanvas(w, h)
  const c = canvas.getContext('2d')!
  c.font = cssFont(st)
  c.fillStyle = st.hex
  c.textBaseline = 'middle'
  c.textAlign = 'center'
  cols.forEach((col, ci) => {
    const cx = layer.position.x - originX - ci * colW
    let y = layer.position.y - originY + st.fontSize / 2
    for (const ch of col.text) {
      if (isUpright(ch)) {
        c.fillText(VERT_PUNCT[ch] ?? ch, cx, y)
      } else {
        c.save()
        c.translate(cx, y)
        c.rotate(Math.PI / 2)
        c.fillText(ch, 0, 0)
        c.restore()
      }
      y += advance
    }
  })
  const img = c.getImageData(0, 0, w, h)
  return { rect: { x: originX, y: originY, w, h }, image: { width: w, height: h, data: img.data }, lines: cols }
}
